import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Switch,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import YearPicker from '../common/YearPicker';
import { useAppSelector } from '../../store/hooks';
import { endpoints } from '../../services/apiClient';
import { NEEView, Unit } from '../../types/enums';
import type { BaselineAnalysisData } from '../../types/schema';
import BaselineChart from '../charts/BaselineChart';


const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

export const HistoricalBaselineTool: React.FC = () => {
    const [startYear, setStartYear] = useState<number | null>(null);
    const [endYear, setEndYear] = useState<number | null>(null);
    const [baselinePeriod, setBaselinePeriod] = useState(4);
    const [unit, setUnit] = useState<Unit>(Unit.TCO2);
    const [view, setView] = useState<NEEView>(NEEView.COMPARISON);
    const [data, setData] = useState<BaselineAnalysisData | null>(null);
    const [loading, setLoading] = useState(false);

    const { selectedPolygonIds } = useAppSelector((state) => state.dataExplorer);
    const { selectedAccount } = useAppSelector((state) => state.app);
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [firstYear, setFirstYear] = useState<number | null>(null);

    const baselinePeriodOptions = useMemo(() => {
        if (!startYear || !firstYear || startYear <= firstYear) return [];
        const maxPeriod = startYear - firstYear;
        return Array.from({ length: maxPeriod }, (_, i) => i + 1);
    }, [startYear, firstYear]);

    // Fetch available years from dedicated endpoint (same as Analytics)
    useEffect(() => {
        const fetchYears = async () => {
            try {
                const response = await endpoints.dashboardAvailableYears(selectedAccount?.accountId);
                if (response.years && response.years.length > 0) {
                    setAvailableYears(response.years);
                    // Store first year of available data
                    const firstYearValue = response.min_year !== null && response.min_year !== undefined
                        ? response.min_year
                        : response.years[0];
                    setFirstYear(firstYearValue);

                    const maxYearValue = response.max_year !== null && response.max_year !== undefined
                        ? response.max_year
                        : response.years[response.years.length - 1];

                    // Set default start year to be one year after the first available year, if possible
                    if (firstYearValue < maxYearValue) {
                        setStartYear(firstYearValue + 1);
                    } else {
                        // If only one year of data, baseline is not possible anyway. Set to the only year.
                        setStartYear(firstYearValue);
                    }
                    setEndYear(maxYearValue);
                } else {
                    // Fallback if no years available
                    const currentYear = new Date().getFullYear();
                    setFirstYear(2000);
                    setStartYear(2000);
                    setEndYear(currentYear);
                }
            } catch (error) {
                console.error('Failed to fetch available years:', error);
                // Fallback on error
                const currentYear = new Date().getFullYear();
                setFirstYear(2000);
                setStartYear(2000);
                setEndYear(currentYear);
            }
        };
        fetchYears();
    }, [selectedAccount]);

    useEffect(() => {
        if (baselinePeriodOptions.length > 0) {
            if (!baselinePeriodOptions.includes(baselinePeriod)) {
                setBaselinePeriod(baselinePeriodOptions[baselinePeriodOptions.length - 1]);
            }
        } else {
            // Reset baselinePeriod when dropdown becomes unavailable
            setBaselinePeriod(1);
        }
    }, [baselinePeriodOptions, baselinePeriod]);

    useEffect(() => {
        const fetchData = async () => {
        if (selectedPolygonIds.length === 0 || !startYear || !endYear) {
            setData(null);
            return;
        }

        setLoading(true);
        try {
            const result = await endpoints.baselineAnalysis(
                startYear,
                endYear,
                baselinePeriod,
                selectedPolygonIds,
                undefined, // months not implemented yet
                selectedAccount?.accountId
            ) as BaselineAnalysisData;
            setData(result);
        } catch (error) {
            console.error('Failed to fetch baseline analysis:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
        };

        fetchData();
    }, [startYear, endYear, baselinePeriod, selectedPolygonIds, selectedAccount]);

    // Show loading state while years are being fetched
    if (startYear === null || endYear === null) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <Typography color="text.secondary">Loading available years...</Typography>
            </Box>
        );
    }

  return (
    <Box pb={3}>
      <FilterContainer elevation={2}>
        <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap" alignItems="flex-start">
          <Box sx={{ width: { xs: '100%', sm: '50%', md: '20%' } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', pb: 2 }}>Start Year of Intervention</Typography>
            <YearPicker
              label=""
              value={new Date(startYear, 0, 1)}
              onChange={(date) => date && setStartYear(date.getFullYear())}
              minDate={firstYear ? new Date(firstYear + 1, 0, 1) : new Date(2000, 0, 1)}
              maxDate={new Date(endYear, 11, 31)}
              availableYears={availableYears.length > 0 ? availableYears : undefined}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              This is the year in which the intervention or change began.
            </Typography>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '50%', md: '20%' } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', pb: 2 }}>End Year of Intervention</Typography>
            <YearPicker
              label=""
              value={new Date(endYear, 0, 1)}
              onChange={(date) => date && setEndYear(date.getFullYear())}
              minDate={new Date(startYear, 0, 1)}
              maxDate={new Date()}
              availableYears={availableYears.length > 0 ? availableYears : undefined}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              This is the final year of data to be included.
            </Typography>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '50%', md: '15%' } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', pb: 2 }}>Baseline Period</Typography>
            <FormControl sx={{ minWidth: 100 }}>
              {/* <InputLabel>Period</InputLabel> */}
              <Select
                value={baselinePeriod}
                label=""
                onChange={(e) => setBaselinePeriod(e.target.value as number)}
                disabled={!startYear || !firstYear || startYear <= firstYear}
              >
                {baselinePeriodOptions.map(year => (
                  <MenuItem key={year} value={year}>{`${year}-yr average`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="text.secondary">
              {!startYear || !firstYear || startYear <= firstYear
                ? 'Select a start year after the first year of available data.'
                : 'Number of years before the project period to average.'}
            </Typography>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '50%', md: '12%' } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', pb: 2 }}>Select Units</Typography>
            <FormControl sx={{ minWidth: 100 }}>
              {/* <InputLabel>Unit</InputLabel> */}
              <Select
                value={unit}
                label=""
                onChange={(e) => setUnit(e.target.value as Unit)}
              >
                <MenuItem value={Unit.TCO2}>NEE tCO 2</MenuItem>
                <MenuItem value={Unit.KTCO2}>NEE ktCO 2</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '50%', md: '15%' } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', pb: 2 }}>Switch Views</Typography>
            <Stack direction="row" spacing={0} alignItems="center">
              <Typography>Comparison</Typography>
              <Switch
                checked={view === NEEView.DIFFERENCE}
                onChange={(e) => setView(e.target.checked ? NEEView.DIFFERENCE : NEEView.COMPARISON)}
              />
              <Typography>Difference</Typography>
            </Stack>
          </Box>
        </Stack>
      </FilterContainer>

      <Box my={1}>
        <Typography variant="h6">Annual NEE vs. Historical Baseline for Selected Polygons</Typography>
        {data ? (
          <BaselineChart
            data={data.chartData}
            baselineAverage={data.baselineAverage}
            view={view}
            unit={unit}
            loading={loading}
          />
        ) : (
            <Box sx={{ height: 400, border: '1px dashed grey', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>
                    {loading ? 'Loading chart...' : 'No data available for the selected criteria.'}
                </Typography>
            </Box>
        )}
      </Box>

      {data && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Box sx={{ width: '100%', maxWidth: 1200 }}>
            <Stack spacing={4} sx={{pt: 4}}>
              {/* Baseline Value */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 260px' }, alignItems: 'center', columnGap: 3 }}>
                <Box sx={{ textAlign: 'right', pr: { md: 2 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Baseline Value</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Average NEE for the selected baseline period.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: data.baselineAverage !== null && data.baselineAverage !== undefined
                      ? (data.baselineAverage < 0 ? 'success.main' : 'error.main')
                      : 'grey.300',
                    color: data.baselineAverage !== null && data.baselineAverage !== undefined
                      ? 'common.white'
                      : 'text.primary',
                    px: 2.5,
                    py: 1.25,
                    borderRadius: 9999,
                    width: { md: 260 },
                    minWidth: { md: 260 },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {`${data.baselineAverage?.toFixed(2) ?? 'N/A'} ${unit === Unit.TCO2 ? 'tCO₂' : 'ktCO₂'}`}
                  </Typography>
                </Box>
              </Box>

              {/* Project Period Comparison */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 260px' }, alignItems: 'center', columnGap: 3 }}>
                <Box sx={{ textAlign: 'right', pr: { md: 2 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Project Period Comparison to Baseline</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Cumulative difference between each project period year and the baseline.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: data.cumulativeDifferenceSum !== null && data.cumulativeDifferenceSum !== undefined
                      ? (data.cumulativeDifferenceSum < 0 ? 'success.main' : 'error.main')
                      : 'grey.300',
                    color: data.cumulativeDifferenceSum !== null && data.cumulativeDifferenceSum !== undefined
                      ? 'common.white'
                      : 'text.primary',
                    px: 2.5,
                    py: 1.25,
                    borderRadius: 9999,
                    width: { md: 260 },
                    minWidth: { md: 260 },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {`${data.cumulativeDifferenceSum?.toFixed(2) ?? 'N/A'} ${unit === Unit.TCO2 ? 'tCO₂' : 'ktCO₂'}`}
                  </Typography>
                </Box>
              </Box>

              {/* Final Year Comparison */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 260px' }, alignItems: 'center', columnGap: 3 }}>
                <Box sx={{ textAlign: 'right', pr: { md: 2 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Final Year Comparison to Baseline</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Difference between the final project period year and the baseline.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: data.finalYearComparison !== null && data.finalYearComparison !== undefined
                      ? (data.finalYearComparison < 0 ? 'success.main' : 'error.main')
                      : 'grey.300',
                    color: data.finalYearComparison !== null && data.finalYearComparison !== undefined
                      ? 'common.white'
                      : 'text.primary',
                    px: 2.5,
                    py: 1.25,
                    borderRadius: 9999,
                    width: { md: 260 },
                    minWidth: { md: 260 },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {`${data.finalYearComparison?.toFixed(2) ?? 'N/A'} ${unit === Unit.TCO2 ? 'tCO₂' : 'ktCO₂'}`}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};
